/**
 * Song sections on the client (2026-09-25) — pure helpers for the Lesson
 * Dashboard's section chips, the grouped note list and the labelled lyrics.
 *
 * The sections themselves are written by the `labelSongSections` Cloud
 * Function (functions/songSections.js, which documents the stored shape):
 *   repertoire.sections     [{ id, label, startLine }] in song order
 *   repertoire.sectionsMeta { source, lyricsHash, ... }
 * A Song Note carries `sectionId` + `sectionLabel` (null = the whole song).
 *
 * A song with NO lyrics still gets chips: the generic set below, never
 * stored, so a teacher can file a note under "Chorus" on day one.
 */
import { WARMUPS_SONG_ID } from './songNotesModel';

export const GENERIC_SECTIONS = [
	{ id: 'g-intro', label: 'Intro' },
	{ id: 'g-verse', label: 'Verse' },
	{ id: 'g-prechorus', label: 'Pre-Chorus' },
	{ id: 'g-chorus', label: 'Chorus' },
	{ id: 'g-bridge', label: 'Bridge' },
	{ id: 'g-outro', label: 'Outro' },
];

/**
 * Matches functions/songSections.js → SECTIONS_VERSION. Machine-made sections
 * older than this are re-detected on the next open (a teacher's 'edited' set
 * never is); until the new set arrives, the old one keeps showing.
 */
export const SECTIONS_VERSION = 2;

const HEADER_RE = /^\s*[[(]\s*([^\])]{2,40})\s*[\])]\s*:?\s*$/;

const splitLines = (text) => String(text || '').replace(/\r\n?/g, '\n').split('\n').slice(0, 400);

/** VERBATIM copy of functions/songSections.js → lyricsHash; both suites pin 'fe135442'. */
export const lyricsHash = (text) => {
	const s = splitLines(text).map((l) => l.trim()).join('\n').trim();
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return h.toString(16).padStart(8, '0');
};

export const lyricsTextOf = (lyrics) => {
	if (!lyrics) return '';
	if (typeof lyrics === 'string') return lyrics;
	return typeof lyrics.text === 'string' ? lyrics.text : '';
};

/** Stored sections that still match this song's lyrics, or null. */
export const storedSections = (song) => {
	const text = lyricsTextOf(song?.lyrics);
	if (!text.trim() || !Array.isArray(song?.sections) || !song.sections.length) return null;
	if (song.sectionsMeta?.lyricsHash && song.sectionsMeta.lyricsHash !== lyricsHash(text)) return null;
	return song.sections;
};

/** The chips a song offers: its own detected sections, else the generic set; none on Warm-ups. */
export const sectionsForSong = (song) => {
	if (!song || song.id === WARMUPS_SONG_ID) return [];
	return storedSections(song) || GENERIC_SECTIONS;
};

/**
 * Should the page ask the function for sections? Only for a real, saved song
 * with lyrics whose stored set is missing or was built from other lyrics.
 */
export const needsDetection = (song) => {
	if (!song || song.id === WARMUPS_SONG_ID || /^(local|pending)-/.test(String(song.id))) return false;
	const text = lyricsTextOf(song.lyrics);
	if (!text.trim()) return false;
	if (!storedSections(song)) return true;
	const meta = song.sectionsMeta || {};
	return meta.source !== 'edited' && (meta.v || 1) < SECTIONS_VERSION;
};

/**
 * Split / merge for the sections sheet (2026-09-26). Both return a NEW array in
 * song order; ids of untouched sections are kept, so notes filed under them
 * stay put. A merged-away section's notes keep their stored label and show
 * under it (groupNotesBySection never drops a note).
 */
export const splitSectionAt = (sections, sectionId, startLine) => {
	const i = sections.findIndex((s) => s.id === sectionId);
	if (i < 0) return sections;
	const next = sections[i + 1];
	if (startLine <= sections[i].startLine || (next && startLine >= next.startLine)) return sections;
	const used = new Set(sections.map((s) => s.id));
	let n = sections.length + 1;
	while (used.has(`s${n}`)) n++;
	const added = { id: `s${n}`, label: `${sections[i].label} cont.`, startLine };
	return [...sections.slice(0, i + 1), added, ...sections.slice(i + 1)];
};

export const mergeWithPrevious = (sections, sectionId) => {
	const i = sections.findIndex((s) => s.id === sectionId);
	if (i <= 0) return sections;
	return sections.filter((s) => s.id !== sectionId);
};

/** The lyric lines a section covers, with their real line indices (for "split here"). */
export const sectionLines = (text, sections, sectionId) => {
	const lines = splitLines(text);
	const sorted = [...sections].sort((a, b) => a.startLine - b.startLine);
	const i = sorted.findIndex((s) => s.id === sectionId);
	if (i < 0) return [];
	const end = i + 1 < sorted.length ? sorted[i + 1].startLine : lines.length;
	const out = [];
	for (let n = sorted[i].startLine; n < end; n++) {
		if (lines[n] && lines[n].trim() && !HEADER_RE.test(lines[n])) out.push({ line: n, text: lines[n].trim() });
	}
	return out;
};

/**
 * Group notes (already in written order) by section, in SONG order.
 * - notes with no section come first, under `null` ("Whole song")
 * - a note whose section id is gone (re-detected) re-attaches by label, else
 *   gets its own trailing group under its stored label, so nothing vanishes
 * - empty groups are dropped
 * Returns { grouped: boolean, groups: [{ key, label, notes }] }; `grouped` is
 * false when no note has a section, so the list renders flat as before.
 */
export const groupNotesBySection = (notes = [], sections = []) => {
	const anySectioned = notes.some((n) => n?.sectionId);
	if (!anySectioned) return { grouped: false, groups: [{ key: 'all', label: null, notes }] };
	const byId = new Map(sections.map((s) => [s.id, s]));
	const byLabel = new Map(sections.map((s) => [s.label.toLowerCase(), s]));
	const buckets = new Map([['none', { key: 'none', label: null, notes: [] }]]);
	sections.forEach((s) => buckets.set(s.id, { key: s.id, label: s.label, notes: [] }));
	for (const note of notes) {
		if (!note?.sectionId) { buckets.get('none').notes.push(note); continue; }
		const home = byId.get(note.sectionId) || byLabel.get(String(note.sectionLabel || '').toLowerCase());
		if (home) { buckets.get(home.id).notes.push(note); continue; }
		const key = `orphan:${note.sectionLabel || note.sectionId}`;
		if (!buckets.has(key)) buckets.set(key, { key, label: note.sectionLabel || 'Earlier section', notes: [] });
		buckets.get(key).notes.push(note);
	}
	return { grouped: true, groups: [...buckets.values()].filter((g) => g.notes.length) };
};

/**
 * The lyrics as blocks for rendering with section labels: [{ section, lines }].
 * Bracketed header lines ("[Chorus]") are dropped because the label replaces
 * them. Lines before the first section (rare) form a label-less block.
 */
export const lyricsBlocks = (text, sections) => {
	const lines = splitLines(text);
	if (!Array.isArray(sections) || !sections.length) return [{ section: null, lines }];
	const starts = [...sections].sort((a, b) => a.startLine - b.startLine);
	const blocks = [];
	if (starts[0].startLine > 0) blocks.push({ section: null, lines: lines.slice(0, starts[0].startLine) });
	starts.forEach((s, i) => {
		const end = i + 1 < starts.length ? starts[i + 1].startLine : lines.length;
		blocks.push({ section: s, lines: lines.slice(s.startLine, end) });
	});
	return blocks
		.map((b) => ({ ...b, lines: trimBlank(b.lines.filter((l) => !HEADER_RE.test(l))) }))
		.filter((b) => b.lines.length || b.section);
};

const trimBlank = (lines) => {
	let a = 0;
	let z = lines.length;
	while (a < z && !lines[a].trim()) a++;
	while (z > a && !lines[z - 1].trim()) z--;
	return lines.slice(a, z);
};

/** First lyric line of a section, for the rename sheet's preview. */
export const sectionPreview = (text, section) => {
	const block = lyricsBlocks(text, [section]).find((b) => b.section);
	return block?.lines.find((l) => l.trim()) || '';
};
